import React, { useState, useEffect, useRef } from "react";
import { Download, Printer, X, BarChart3, Loader } from "lucide-react";

const AdminReportGenerator = ({
  isOpen,
  onClose,
  adminData,
  dashboardStats,
  allPets,
  allAppointments,
  doctors,
  allUsersData,
}) => {
  const [reportData, setReportData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    if (isOpen && dashboardStats) {
      generateReportData();
    }
  }, [isOpen, dashboardStats]);

  const generateReportData = () => {
    setIsGenerating(true);

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const appointments = Array.isArray(allAppointments) ? allAppointments : [];

    const monthlyAppointments = appointments.filter((apt) => {
      const aptDate = new Date(apt.date);
      return (
        aptDate.getMonth() === currentMonth &&
        aptDate.getFullYear() === currentYear
      );
    });

    const appointmentStats = {
      total: appointments.length,
      monthly: monthlyAppointments.length,
      completed: appointments.filter((apt) => apt.status === "completed")
        .length,
      pending: appointments.filter((apt) => apt.status === "pending").length,
      confirmed: appointments.filter((apt) => apt.status === "confirmed")
        .length,
      cancelled: appointments.filter((apt) => apt.status === "cancelled")
        .length,
    };

    const completionRate =
      appointmentStats.total > 0
        ? ((appointmentStats.completed / appointmentStats.total) * 100).toFixed(
            1
          )
        : 0;

    const pets = Array.isArray(allPets) ? allPets : [];
    const petsBySpecies = pets.reduce((acc, pet) => {
      const species = pet.species || "Unknown";
      acc[species] = (acc[species] || 0) + 1;
      return acc;
    }, {});

    const users = Array.isArray(allUsersData) ? allUsersData : [];
    const usersByRole = users.reduce((acc, user) => {
      const role = user.role || "User";
      acc[role] = (acc[role] || 0) + 1;
      return acc;
    }, {});

    const doctorsData = Array.isArray(doctors) ? doctors : [];
    const doctorStats = doctorsData.map((doctor) => {
      const doctorAppointments = appointments.filter(
        (apt) => apt.doctorId === doctor._id || apt.doctor?._id === doctor._id
      );
      return {
        name: doctor.name || doctor.userDetails?.name || "Unknown",
        email: doctor.email || doctor.userDetails?.email || "N/A",
        appointments: doctorAppointments.length,
        completedAppointments: doctorAppointments.filter(
          (apt) => apt.status === "completed"
        ).length,
      };
    });

    const recentAppointments = appointments
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 10);

    const petAppointmentCounts = appointments.reduce((acc, apt) => {
      const petId = apt.pets?._id || apt.petId;
      if (petId) {
        acc[petId] = (acc[petId] || 0) + 1;
      }
      return acc;
    }, {});

    const topPets = pets
      .map((pet) => ({
        ...pet,
        appointmentCount: petAppointmentCounts[pet._id] || 0,
      }))
      .sort((a, b) => b.appointmentCount - a.appointmentCount)
      .slice(0, 5);

    setReportData({
      admin: adminData,
      generatedDate: currentDate.toLocaleDateString(),
      period: `${currentDate.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      })}`,
      stats: {
        ...dashboardStats,
        ...appointmentStats,
        completionRate,
        totalPets: pets.length,
        totalUsers: users.length,
        totalDoctors: doctorsData.length,
      },
      breakdowns: {
        petsBySpecies,
        usersByRole,
        appointmentsByStatus: {
          completed: appointmentStats.completed,
          pending: appointmentStats.pending,
          confirmed: appointmentStats.confirmed,
          cancelled: appointmentStats.cancelled,
        },
      },
      doctorPerformance: doctorStats,
      recentAppointments,
      topPets,
      insights: {
        mostActiveDoctor: doctorStats.reduce(
          (prev, current) =>
            prev.appointments > current.appointments ? prev : current,
          doctorStats[0] || {}
        ),
        busiestDay: "Monday",
        averageAppointmentsPerDay: Math.round(appointmentStats.monthly / 30),
      },
    });

    setIsGenerating(false);
  };

  const handlePrint = async () => {
    setIsExporting(true);
    try {
      const script = document.createElement("script");
      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
      document.head.appendChild(script);

      script.onload = async () => {
        const element = printRef.current;
        const canvas = await window.html2canvas(element, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
        });

        const link = document.createElement("a");
        link.href = canvas.toDataURL("image/png");
        link.download = `Admin_Report_${
          new Date().toISOString().split("T")[0]
        }.png`;
        link.click();

        setIsExporting(false);
      };
    } catch (error) {
      console.error("Error generating print:", error);
      setIsExporting(false);
    }
  };

  const handleDownloadPDF = async () => {
    setIsExporting(true);
    try {
      const html2canvasScript = document.createElement("script");
      html2canvasScript.src =
        "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
      document.head.appendChild(html2canvasScript);

      const jsPDFScript = document.createElement("script");
      jsPDFScript.src =
        "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
      document.head.appendChild(jsPDFScript);

      html2canvasScript.onload = () => {
        jsPDFScript.onload = async () => {
          const element = printRef.current;
          const canvas = await window.html2canvas(element, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: "#ffffff",
          });

          const imgData = canvas.toDataURL("image/png");
          const pdf = new window.jsPDF.jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4",
          });

          const imgWidth = 210;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          let heightLeft = imgHeight;
          let position = 0;

          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= 297;

          while (heightLeft >= 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
            heightLeft -= 297;
          }

          pdf.save(
            `Admin_Report_${new Date().toISOString().split("T")[0]}.pdf`
          );
          setIsExporting(false);
        };
      };
    } catch (error) {
      console.error("Error generating PDF:", error);
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <BarChart3 className="w-6 h-6 mr-2 text-blue-600" />
              Admin Dashboard Report
            </h2>
            <p className="text-gray-600 mt-1">
              Comprehensive practice overview and analytics
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrint}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors disabled:opacity-50"
              disabled={isGenerating || isExporting}
            >
              {isExporting ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Printer className="w-4 h-4" />
              )}
              <span>Download</span>
            </button>
            {/* <button
              onClick={handleDownloadPDF}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors disabled:opacity-50"
              disabled={isGenerating || isExporting}
            >
              {isExporting ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              <span>Download as PDF</span>
            </button> */}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Report Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {isGenerating ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">
                  Generating comprehensive report...
                </p>
              </div>
            </div>
          ) : reportData ? (
            <div ref={printRef} className="bg-white p-12 rounded-lg shadow">
              {/* Report Header */}
              <div className="text-center mb-8 border-b-2 border-blue-600 pb-6">
                <h1 className="text-4xl font-bold text-blue-600 mb-2">
                  Veterinary Practice Management Report
                </h1>
                <h2 className="text-2xl text-gray-600 mb-6">
                  Administrative Dashboard Analysis
                </h2>

                <div className="text-left max-w-2xl mx-auto space-y-2 text-sm">
                  <div>
                    <span className="font-bold">Administrator:</span>{" "}
                    {reportData.admin?.name || "System Administrator"}
                  </div>
                  <div>
                    <span className="font-bold">Report Period:</span>{" "}
                    {reportData.period}
                  </div>
                  <div>
                    <span className="font-bold">Generated:</span>{" "}
                    {reportData.generatedDate}
                  </div>
                  <div>
                    <span className="font-bold">Report Type:</span>{" "}
                    Comprehensive Practice Overview
                  </div>
                </div>
              </div>

              {/* Executive Summary */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-blue-600 mb-4 border-b pb-2">
                  Executive Summary
                </h3>

                <div className="grid grid-cols-4 gap-4 mb-8">
                  {[
                    {
                      label: "Total Pets",
                      value: reportData.stats.totalPets,
                    },
                    {
                      label: "Total Appointments",
                      value: reportData.stats.total,
                    },
                    {
                      label: "Active Doctors",
                      value: reportData.stats.totalDoctors,
                    },
                    {
                      label: "Registered Users",
                      value: reportData.stats.totalUsers,
                    },
                  ].map((stat, i) => (
                    <div
                      key={i}
                      className="border rounded-lg p-4 text-center bg-gray-50"
                    >
                      <div className="text-3xl font-bold text-blue-600">
                        {stat.value}
                      </div>
                      <div className="text-xs font-bold text-gray-600 uppercase mt-2">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-blue-50 p-4 rounded mb-6">
                  <h4 className="font-bold text-gray-900 mb-3">
                    Key Performance Indicators
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <strong>Appointment Completion Rate:</strong>{" "}
                      {reportData.stats.completionRate}%
                    </div>
                    <div>
                      <strong>Monthly Appointments:</strong>{" "}
                      {reportData.stats.monthly}
                    </div>
                    <div>
                      <strong>Average Daily Appointments:</strong>{" "}
                      {reportData.insights.averageAppointmentsPerDay}
                    </div>
                    <div>
                      <strong>Most Active Doctor:</strong>{" "}
                      {reportData.insights.mostActiveDoctor?.name || "N/A"} (
                      {reportData.insights.mostActiveDoctor?.appointments || 0}{" "}
                      appointments)
                    </div>
                  </div>
                </div>
              </div>

              {/* Detailed Breakdowns */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-blue-600 mb-4 border-b pb-2">
                  Detailed Breakdowns
                </h3>

                <div className="grid grid-cols-2 gap-8 mb-8">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">
                      Appointment Status Distribution
                    </h4>
                    <div className="space-y-2 text-sm">
                      {Object.entries(
                        reportData.breakdowns.appointmentsByStatus
                      ).map(([status, count]) => (
                        <div
                          key={status}
                          className="flex justify-between border-b pb-1"
                        >
                          <span className="capitalize">{status}:</span>
                          <strong>{count}</strong>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">
                      Pets by Species
                    </h4>
                    <div className="space-y-2 text-sm">
                      {Object.entries(reportData.breakdowns.petsBySpecies).map(
                        ([species, count]) => (
                          <div
                            key={species}
                            className="flex justify-between border-b pb-1"
                          >
                            <span className="capitalize">{species}:</span>
                            <strong>{count}</strong>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Doctor Performance */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-blue-600 mb-4 border-b pb-2">
                  Doctor Performance Summary
                </h3>

                <table className="w-full border-collapse mb-8 text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-3 text-left font-bold">
                        Doctor Name
                      </th>
                      <th className="border p-3 text-left font-bold">Email</th>
                      <th className="border p-3 text-left font-bold">
                        Total Appointments
                      </th>
                      <th className="border p-3 text-left font-bold">
                        Completed
                      </th>
                      <th className="border p-3 text-left font-bold">
                        Success Rate
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.doctorPerformance.map((doctor, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="border p-3">{doctor.name}</td>
                        <td className="border p-3">{doctor.email}</td>
                        <td className="border p-3 text-center">
                          {doctor.appointments}
                        </td>
                        <td className="border p-3 text-center">
                          {doctor.completedAppointments}
                        </td>
                        <td className="border p-3 text-center">
                          {doctor.appointments > 0
                            ? Math.round(
                                (doctor.completedAppointments /
                                  doctor.appointments) *
                                  100
                              )
                            : 0}
                          %
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Top Pets */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-blue-600 mb-4 border-b pb-2">
                  Most Active Pets
                </h3>

                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-3 text-left font-bold">
                        Pet Name
                      </th>
                      <th className="border p-3 text-left font-bold">
                        Species
                      </th>
                      <th className="border p-3 text-left font-bold">Breed</th>
                      <th className="border p-3 text-left font-bold">Owner</th>
                      <th className="border p-3 text-left font-bold">
                        Appointments
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.topPets.map((pet, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="border p-3">{pet.name || "Unnamed"}</td>
                        <td className="border p-3 capitalize">
                          {pet.species || "Unknown"}
                        </td>
                        <td className="border p-3">{pet.breed || "Unknown"}</td>
                        <td className="border p-3">
                          {pet.ownerId?.userDetails?.name || "Unknown"}
                        </td>
                        <td className="border p-3 text-center">
                          {pet.appointmentCount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Recent Appointments */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-blue-600 mb-4 border-b pb-2">
                  Recent Appointments
                </h3>

                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-3 text-left font-bold">Date</th>
                      <th className="border p-3 text-left font-bold">Pet</th>
                      <th className="border p-3 text-left font-bold">Owner</th>
                      <th className="border p-3 text-left font-bold">Type</th>
                      <th className="border p-3 text-left font-bold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.recentAppointments.map((apt, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="border p-3">
                          {new Date(apt.date).toLocaleDateString()}
                        </td>
                        <td className="border p-3">
                          {apt.pets?.name || "Unknown"}
                        </td>
                        <td className="border p-3">
                          {apt.pets?.ownerId?.userDetails?.name ||
                            apt.pets?.owner?.name ||
                            "Unknown"}
                        </td>
                        <td className="border p-3 capitalize">{apt.type}</td>
                        <td className="border p-3 capitalize">{apt.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* System Health */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-blue-600 mb-4 border-b pb-2">
                  System Health Indicators
                </h3>

                <div className="grid grid-cols-3 gap-4">
                  {[
                    {
                      label: "Appointment Success",
                      value:
                        reportData.stats.completionRate > 85
                          ? "Excellent"
                          : reportData.stats.completionRate > 70
                          ? "Good"
                          : "Needs Attention",
                      bgColor:
                        reportData.stats.completionRate > 85
                          ? "bg-green-100"
                          : reportData.stats.completionRate > 70
                          ? "bg-yellow-100"
                          : "bg-red-100",
                      textColor:
                        reportData.stats.completionRate > 85
                          ? "text-green-700"
                          : reportData.stats.completionRate > 70
                          ? "text-yellow-700"
                          : "text-red-700",
                    },
                    {
                      label: "Doctor Coverage",
                      value:
                        reportData.stats.totalDoctors >= 3
                          ? "Adequate"
                          : "Limited",
                      bgColor:
                        reportData.stats.totalDoctors >= 3
                          ? "bg-green-100"
                          : "bg-yellow-100",
                      textColor:
                        reportData.stats.totalDoctors >= 3
                          ? "text-green-700"
                          : "text-yellow-700",
                    },
                    {
                      label: "Daily Volume",
                      value:
                        reportData.insights.averageAppointmentsPerDay > 15
                          ? "High"
                          : reportData.insights.averageAppointmentsPerDay > 8
                          ? "Moderate"
                          : "Low",
                      bgColor:
                        reportData.insights.averageAppointmentsPerDay > 15
                          ? "bg-green-100"
                          : reportData.insights.averageAppointmentsPerDay > 8
                          ? "bg-yellow-100"
                          : "bg-red-100",
                      textColor:
                        reportData.insights.averageAppointmentsPerDay > 15
                          ? "text-green-700"
                          : reportData.insights.averageAppointmentsPerDay > 8
                          ? "text-yellow-700"
                          : "text-red-700",
                    },
                  ].map((health, i) => (
                    <div
                      key={i}
                      className={`${health.bgColor} rounded-lg p-4 text-center`}
                    >
                      <div className={`text-xl font-bold ${health.textColor}`}>
                        {health.value}
                      </div>
                      <div className="text-xs font-bold text-gray-700 uppercase mt-2">
                        {health.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Key Insights */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-blue-600 mb-4 border-b pb-2">
                  Key Insights & Recommendations
                </h3>

                <div className="bg-blue-50 border border-blue-300 rounded-lg p-6 space-y-3 text-sm">
                  <div>
                    <strong>Practice Growth:</strong> Currently managing{" "}
                    {reportData.stats.totalPets} pets with{" "}
                    {reportData.stats.totalDoctors} active doctors serving{" "}
                    {reportData.stats.totalUsers} registered users.
                  </div>
                  <div>
                    <strong>Appointment Efficiency:</strong>{" "}
                    {reportData.stats.completionRate}% completion rate indicates{" "}
                    {reportData.stats.completionRate > 85
                      ? "excellent"
                      : reportData.stats.completionRate > 70
                      ? "good"
                      : "room for improvement"}{" "}
                    operational efficiency.
                  </div>
                  <div>
                    <strong>Resource Utilization:</strong> With{" "}
                    {reportData.insights.averageAppointmentsPerDay} appointments
                    per day, the practice maintains{" "}
                    {reportData.insights.averageAppointmentsPerDay > 20
                      ? "high"
                      : reportData.insights.averageAppointmentsPerDay > 10
                      ? "moderate"
                      : "low"}{" "}
                    utilization.
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="text-center text-xs text-gray-600 border-t pt-6 mt-8">
                <div>
                  This comprehensive report was automatically generated on{" "}
                  {reportData.generatedDate}
                </div>
                <div>
                  Veterinary Practice Management System | Administrative
                  Dashboard
                </div>
                <div className="mt-2">
                  Confidential Document - For Internal Use Only
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AdminReportGenerator;
